import { useTranslation } from 'react-i18next'
import useFormStatus from '@/containers/SubscriptionFormPageNew/SubscriptionForm/useFormStatus'
import { useDispatch, useSelector } from 'react-redux'
import { useStyletron } from 'baseui'
import { getAddressFromMeterInformation } from './pceEligibilityCallback'
import {
  updateAddressData,
  updateGasMeterAddressData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import {
  selectActiveAddress,
  selectAddressCurrentContractActive,
  selectCharacteristicsCurrentContractActive,
  selectConfig,
  selectCurrentAddressData,
  selectPersonalContactSubscriptionData,
  selectPreferencesContactSubscriptionData,
  selectProfessionalContactSubscriptionData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { subscriptionFormActionsV2 } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormV2Sagas'
import { getAddressPrefillData } from '../Address/addressPrefillUtils'
import { modulesConfig } from '@/containers/SubscriptionFormPageNew/config'
import { selectAuthData } from '@/containers/Login/AuthSelectors'

const useGasMeter = ({ type = null }) => {
  const dispatch = useDispatch()
  const [css] = useStyletron()
  const { t } = useTranslation()
  const { mandatoryFields, errors, isEditable, lastSentDate } = useFormStatus()
  const { contactId } = useSelector(selectConfig)
  const { firstName, lastName, zohoId, phone1 } = useSelector(selectPersonalContactSubscriptionData)
  const { dataCollectionAuth, clientType } = useSelector(selectPreferencesContactSubscriptionData)
  const { socialReason: proSocialReason } = useSelector(selectProfessionalContactSubscriptionData)
  const { prestationType, energyType } = useSelector(selectCharacteristicsCurrentContractActive)
  const { country } = useSelector(selectAuthData)
  const grdfAuthHidden = !modulesConfig[country]?.meter?.grdfAuth
  const currentAddressData = useSelector(selectCurrentAddressData)
  const electricityMeter =
    type === 'contract'
      ? useSelector(selectAddressCurrentContractActive).electricityMeter || {}
      : currentAddressData.electricityMeter || {}
  const gasMeter =
    type === 'contract'
      ? useSelector(selectAddressCurrentContractActive).gasMeter || {}
      : currentAddressData.gasMeter || {}

  const {
    pceNo,
    meterSerialNumber,
    meterConsent = false,
    consentDate,
    isValidPce = false,
    pceEligibilityLoading,
    pceEligibilityData,
    pceEligibilityPending,
  } = gasMeter

  const { pdlNo } = electricityMeter

  const activeAddress = useSelector(selectActiveAddress)

  const gasMeterUpdate = payload => {
    dispatch(
      updateGasMeterAddressData({
        data: payload,
        id: activeAddress,
      }),
    )
  }

  const canRequestMeterData =
    pceNo?.length === 14 &&
    zohoId?.length > 0 &&
    lastName?.length > 0 &&
    firstName?.length > 0 &&
    phone1?.length > 0 &&
    dataCollectionAuth

  const treatMeterDataInformation = meterInformation => {
    gasMeterUpdate({
      pceEligibilityData: meterInformation,
      pceEligibilityLoading: false,
    })

    if (meterInformation.gas?.deliveryPoint?.address) {
      const address = getAddressFromMeterInformation(meterInformation.gas?.deliveryPoint?.address)
      if (address !== null) {
        const addressUpdate = getAddressPrefillData(address, currentAddressData)

        if (Object.keys(addressUpdate).length > 0) {
          // Address prefill driven by a GRDF websocket callback, not a user
          // edit ─ skip dirty marking so the global save warning stays accurate.
          dispatch(updateAddressData({ data: addressUpdate, id: activeAddress, _skipDirty: true }))
        }
      }
    }
  }

  const requestPceEligibility = () => {
    dispatch({
      type: subscriptionFormActionsV2.METER_MODULE_REQUEST_PCE_DATA_V2,
      body: {
        meterIdElectricity: pdlNo,
        meterIdGas: pceNo,
        meterType: 'pce',
        lastname: clientType === 'pro' ? proSocialReason : lastName,
        firstname: firstName,
        phone: phone1,
        zohoId,
        consent: true,
        energyType: energyType || 'gas',
        prestationType,
        contactId,
      },
    })
  }

  return {
    t,
    mandatoryFields,
    errors,
    isEditable,
    lastSentDate,
    pceNo,
    meterSerialNumber,
    meterConsent,
    consentDate,
    prestationType,
    isValidPce,
    pceEligibilityLoading,
    pceEligibilityPending,
    pceEligibilityData,
    css,
    canRequestMeterData,
    grdfAuthHidden,
    gasMeterUpdate,
    requestPceEligibility,
    treatMeterDataInformation,
  }
}

export default useGasMeter
